import { motion } from "framer-motion";
import { Zap, DollarSign, GraduationCap, Layers, Gauge } from "lucide-react";

const reasons = [
  {
    icon: Zap,
    label: "Speed",
    stat: "90—120",
    unit: "days to a working desk",
    body:
      "From mandate charter to first deployed IC memo — measured in weeks, not years.",
  },
  {
    icon: DollarSign,
    label: "Cost",
    stat: "40—60%",
    unit: "lower than in-house build",
    body:
      "Shared infrastructure, regional talent, and modular tooling — fully loaded, transparently priced.",
  },
  {
    icon: GraduationCap,
    label: "Expertise",
    stat: "50+",
    unit: "years of aggregate operator DNA",
    body:
      "Partners with operating, investing, and diligence careers across VC, PE, tech, and family capital.",
  },
  {
    icon: Layers,
    label: "Flexibility",
    stat: "5",
    unit: "modules, stacked to the mandate",
    body:
      "Start with one desk — scale into a full capability center as your thesis compounds.",
  },
  {
    icon: Gauge,
    label: "ROI Visibility",
    stat: "MOIC · IRR · Impact",
    unit: "reported quarterly",
    body:
      "Board-grade dashboards benchmarked against target mandates and peer allocators.",
  },
];

export default function WhyAdvik() {
  return (
    <section
      id="outcomes"
      className="relative py-24 md:py-40 bg-ink-2 border-t border-[var(--line)]"
      data-testid="why-advik-section"
    >
      <div className="grain absolute inset-0" />
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 md:mb-20">
          <div className="md:col-span-7">
            <div className="text-[10px] tracking-eyebrow uppercase text-gold mb-4">
              VI. Why Advik
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Faster to signal, cheaper to build, easier to defend at the board.
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
          {reasons.map((r, i) => (
            <motion.div
              key={r.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.2, 0.7, 0.2, 1] }}
              className="group relative border border-[var(--line)] bg-ink/40 p-6 md:p-7 hover:border-gold/60 transition-colors min-h-[280px] flex flex-col justify-between"
              data-testid={`why-advik-${i}`}
            >
              <div className="flex items-center justify-between">
                <r.icon size={22} strokeWidth={1.25} className="text-gold" />
                <span className="text-[10px] tracking-eyebrow uppercase text-ivory-dim">
                  {r.label}
                </span>
              </div>
              <div>
                <div className="font-display text-4xl md:text-5xl text-ivory leading-none">
                  {r.stat}
                </div>
                <div className="mt-2 text-[10px] tracking-eyebrow uppercase text-gold">
                  {r.unit}
                </div>
                <p className="mt-4 text-ivory/60 text-sm leading-relaxed">
                  {r.body}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
